import {useEffect, useState} from "react";
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from "../../../basic/dialog";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {TeamInformationForm} from "../../../shared/BillingForm";
import {InvoicePreview} from "./InvoicePreview";
import {
    useGetPortalTeamQuery,
    useUpdatePortalTeamMutation,
    useUpdateProfileMutation
} from "../../../../store/generated/api";
import {toast} from "sonner";
import {Button} from "../../../basic/button";

interface InvoiceRequestDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSubmit: (productKey: string, quantity: number, vatNumber?: string) => void;
    productInfo: {
        productName: string;
        productKey: string;
        quantity: number;
        purchasePrice: number;
        supportPrice: number;
        currency: string;
        pricingType: string;
        licenseDurationLabel?: string;
        packageSize?: number;
    };
    title?: string;
    description?: string;
    requireVat?: boolean;
}

export function InvoiceRequestDialog({
                                         open,
                                         onOpenChange,
                                         onSubmit,
                                         productInfo,
                                         title,
                                         description,
                                         requireVat = false
                                     }: InvoiceRequestDialogProps) {
    const {t} = useTranslationWithKeys();
    const {data: teamData, isLoading, refetch} = useGetPortalTeamQuery({
        page: 0,
        size: 10
    });
    const team = teamData?.team;
    const [updatePortalTeam, {isLoading: isUpdatingTeam}] = useUpdatePortalTeamMutation();
    const [updateProfile, {isLoading: isUpdatingProfile}] = useUpdateProfileMutation();
    const [formValid, setFormValid] = useState(false);

    useEffect(() => {
        if (!open) {
            setFormValid(false);
        }
    }, [open]);

    const handleSubmit = async (formData: {
        firstName: string;
        lastName: string;
        teamName: string;
        streetAddress: string;
        city: string;
        stateProvince: string;
        postalCode: string;
        country: string;
        phoneNumber: string;
        billingEmail: string;
        vatNumber?: string;
        quantity: number;
    }) => {
        try {
            await Promise.all([
                updatePortalTeam({
                    vwpTeamUpdateRequest: {
                        name: formData.teamName,
                        billingStreetAddress: formData.streetAddress,
                        billingCity: formData.city,
                        billingState: formData.stateProvince,
                        billingPostalCode: formData.postalCode,
                        billingCountry: formData.country,
                        phone: formData.phoneNumber,
                        billingEmail: formData.billingEmail,
                        vatNumber: formData.vatNumber
                    }
                }).unwrap(),
                updateProfile({
                    vwpAuthUserUpdateRequest: {
                        firstName: formData.firstName,
                        lastName: formData.lastName
                    }
                }).unwrap()
            ]);

            await refetch();

            await new Promise(resolve => setTimeout(resolve, 500));

            toast.success(t('settings.teamSettingsSaved'));
            onOpenChange(false);
            onSubmit(productInfo.productKey, formData.quantity, formData.vatNumber);
        } catch (error: any) {
            if (error?.data?.error === 'INVALID_COUNTRY') {
                const invalidCountry = error?.data?.details?.country || formData.country;
                toast.error(t('settings.invalidCountry', {country: invalidCountry}));
            } else {
                toast.error(t('error.generic', {message: t('common.error')}));
            }
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-5xl">
                <DialogHeader>
                    <DialogTitle>{title || t('invoice.requestInvoice')}</DialogTitle>
                    <DialogDescription>
                        {description || t('invoice.requestInvoiceDescription')}
                    </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 py-4">
                    <div>
                        <TeamInformationForm
                            team={team}
                            isLoading={isLoading}
                            onValidChange={setFormValid}
                            onSubmit={handleSubmit}
                            isSubmitting={isUpdatingTeam || isUpdatingProfile}
                            initialQuantity={productInfo.quantity}
                            isTrial={false}
                            showSubmitButton={false}
                            requireVat={requireVat}
                        />
                    </div>
                    <div>
                        <InvoicePreview
                            productName={productInfo.productName}
                            quantity={productInfo.quantity}
                            purchasePrice={productInfo.purchasePrice}
                            supportPrice={productInfo.supportPrice}
                            currency={productInfo.currency}
                            pricingType={productInfo.pricingType}
                            licenseDurationLabel={productInfo.licenseDurationLabel}
                            packageSize={productInfo.packageSize}
                        />
                    </div>
                </div>
                <div className="flex justify-end gap-2 pt-4 border-t">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isUpdatingTeam || isUpdatingProfile}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        type="button"
                        disabled={!formValid || isUpdatingTeam || isUpdatingProfile || isLoading}
                        onClick={() => {
                            const form = document.querySelector('form');
                            if (form) {
                                form.requestSubmit();
                            }
                        }}
                    >
                        {isUpdatingTeam || isUpdatingProfile ? t('common.action.saving') : t('invoice.requestInvoice')}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
